import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  Popup,
  Button,
  Confirm,
  Image,
  Grid,
  GridRow,
  GridColumn,
} from "semantic-ui-react";
import { ENV, buildStorageUrl } from "../../../../../../../utils";
import { CustomQuillEditor } from "../../../../../../Shared";
import { useDropzone } from "react-dropzone";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Smu05 = React.memo((props) => {
  const { index, slideId, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Synchronizes component state with the content of the currently selected slide in Smu05.js.
   *
   * This `useEffect` hook is executed whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state with the slide's title, content, and image.
   *
   * Specifically, it performs the following actions:
   * - Sets the `title` state with the slide's title.
   * - Sets the `content` state with the slide's content.
   * - Sets the `image` state with the slide's image URL.
   *
   * This ensures that the component accurately represents the content of the current slide, allowing for
   * effective editing and previewing within an active session.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTitle(slide.content.title);
      setContent(slide.content.content);
      setImage(slide.content.image);
    }
  }, [slideId]);

  /**
   * Updates the content of a specific slide within the `slidesActiveSessionEdit` array.
   *
   * This `useEffect` hook is triggered whenever there are changes to the `title`, `content`, or `image` states. It begins by finding the index of the slide that matches the `slideId` within the `slidesActiveSessionEdit` array. If the slide is found (indicated by `slideIndex` not being -1), the hook updates the slide's content object with the new values for title, content, and image.
   *
   * Specifically, it performs the following updates:
   * - Sets the slide's title to the new `title`.
   * - Updates the slide's content with the new `content`.
   * - Sets the slide's image URL to the new `image`.
   *
   * This ensures that any changes made to the slide's title, content, or image are immediately reflected within the active session's slide array, maintaining the integrity and accuracy of the session content as it is being edited.
   *
   * @dependency title - The current title value to be updated for the slide.
   * @dependency content - The current content value to be updated for the slide.
   * @dependency image - The current image URL to be updated for the slide.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.title = title;
      slidesActiveSessionEdit[slideIndex].content.content = content;
      slidesActiveSessionEdit[slideIndex].content.image = image;
    }
  }, [title, content, image]);

  /**
   * Event handlers for managing slide content updates in Smu05.js.
   *
   * `handleTitleChange`:
   * - Triggered when the title input field changes.
   * - Updates the `title` state, removing leading whitespace from the input value.
   *
   * `handleContentChange`:
   * - Triggered when the content changes (e.g., text editor content update).
   * - Directly updates the `content` state with the new content.
   *
   * `onDrop`:
   * - A callback used with `useDropzone` for handling dropped files.
   * - Accepts the first file from the dropped files, creates a URL object for it, and updates the `image` state.
   *
   * `useDropzone` configuration:
   * - Configures the dropzone to accept JPEG and PNG images.
   * - Utilizes `onDrop` for handling file drops, updating the `image` state with the URL of the dropped image.
   *
   * These handlers and configurations facilitate the dynamic updating of slide content, including the title, content, and image, allowing for efficient editing within the component.
   */
  const handleTitleChange = (event) => {
    setTitle(event.target.value.trimStart());
  };

  const handleContentChange = (content) => {
    setContent(content);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    onDrop,
  });

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__input">
          <div className="media-slide__label-div">
            <Icon name="bookmark" />
            <h5 className="media-slide__label">Título:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input-title"
            name="title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="content" />
            <h5 className="media-slide__label">Contenido:</h5>
          </div>

          <Grid columns={2} divided>
            <GridRow>
              <GridColumn>
                <div className="media-slide__image" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Image
                    src={image?.startsWith('blob:') ? image : (buildStorageUrl(image) || image)}
                    rounded
                    fluid
                  />
                </div>
              </GridColumn>
              <GridColumn>
                <CustomQuillEditor
                  className="media-slide__input-content"
                  value={content}
                  onChange={handleContentChange}
                />
              </GridColumn>
            </GridRow>
          </Grid>
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
